import * as React from 'react';
import { useState } from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { message, Modal } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { getDisabledDate, getDisabledDateTime, nullSafeGet } from '../../utils/DataAccessUtils';
import { formatDateForLocationTimeZone, wallTimeToUtcIso } from '../../utils/DataFormatterUtils';
import { formatDuration, WrenchModeEvent } from '../../utils/WrenchModeEventUtils';
import { reviseDriveTimeForSupplier } from '../../thunks/wrench_mode_events_thunks';
import { DateTimeWithTimeZone } from '../date_time_with_time_zone/DateTimeWithTimeZone';
import { DriveTripWindow } from './WrenchModeEventTimeline';

interface ReviseDriveTimeModalProps extends FormComponentProps {
	visible: boolean;
	onCancel: () => void;
	onSuccess?: () => void;
	beganEvent: WrenchModeEvent | null;
	arrivedEvent: WrenchModeEvent | null;
	// The schedule location's hydrated `location` (carries timeZoneId for the pickers).
	location: any;
	// This trip's trip_paused/trip_resumed events — not editable, but the edited window must
	// contain them and the preview must subtract them.
	pauseEvents: WrenchModeEvent[];
	// The other trips to this same site on the same day; the edit can't overlap them.
	otherTripWindows: DriveTripWindow[];
	// The visit's date (YYYY-MM-DD) from the schedule — the authoritative day. Only the
	// time-of-day is editable; the date is locked to this.
	scheduleDate: string;

	reviseDriveTime: (retimes: Array<{ id: number; createdAt: string }>) => Promise<any>;
}

const ReviseDriveTimeModalInner: React.FC<ReviseDriveTimeModalProps> = (props) => {
	const {
		visible,
		onCancel,
		onSuccess,
		beganEvent,
		arrivedEvent,
		location,
		pauseEvents,
		otherTripWindows,
		scheduleDate,
		form,
		reviseDriveTime,
	} = props;
	const { getFieldValue } = form;

	const [submitting, setSubmitting] = useState(false);

	const locationTimeZone = nullSafeGet('timeZoneId', location);
	const beganTimeZone = getFieldValue('beganTimeZone') || locationTimeZone;
	const arrivedTimeZone = getFieldValue('arrivedTimeZone') || locationTimeZone;
	const scheduleZone = locationTimeZone || moment.tz.guess();

	// The visit's date is authoritative from the schedule, not the raw event timestamp — so an
	// event whose createdAt drifted to another day can't move the picker off the scheduled day.
	// Build each field's initial value as the schedule date + the event's time-of-day, and lock
	// the calendar to the schedule date so only the time-of-day is editable.
	const composeInitial = (eventCreatedAt: string): string => {
		if (!eventCreatedAt || !scheduleDate) return eventCreatedAt;
		const timeOfDay = moment(eventCreatedAt).tz(scheduleZone).format('HH:mm:ss');
		return moment
			.tz(`${scheduleDate} ${timeOfDay}`, 'YYYY-MM-DD HH:mm:ss', scheduleZone)
			.utc()
			.toISOString();
	};
	const beganInitial = composeInitial(nullSafeGet('createdAt', beganEvent));
	const arrivedInitial = composeInitial(nullSafeGet('createdAt', arrivedEvent));

	const scheduleDisabledDate = (timeZone: string) => {
		const zone = timeZone || scheduleZone;
		if (!scheduleDate) return getDisabledDate(undefined, zone, moment());
		const dayStart = moment.tz(scheduleDate, 'YYYY-MM-DD', zone);
		return getDisabledDate(
			dayStart.toISOString(),
			zone,
			dayStart.clone().endOf('day').toISOString()
		);
	};

	// A drive can end with `arrived` (tech reached the site) or `trip_cancelled` (trip aborted).
	// Both count as drive time, so both are editable — label the end picker to match the event.
	const isCancelledTrip = nullSafeGet('eventType', arrivedEvent) === 'trip_cancelled';
	const endLabel = isCancelledTrip ? 'Trip Cancelled' : 'Arrived';
	const endRequiredMessage = isCancelledTrip
		? 'Please select when the trip was cancelled.'
		: 'Please select when the technician arrived.';
	const endAfterStartMessage = `${endLabel} time must be after the trip start time.`;

	// Constrain arrival against the began value currently in the form (which the
	// user may have just edited), converted to a UTC instant for comparison.
	const editedBeganValue = getFieldValue('beganTime');
	const currentBeganTime = editedBeganValue
		? wallTimeToUtcIso(editedBeganValue, beganTimeZone)
		: beganInitial;

	// Pauses stay fixed. The edited window must still contain them, and the drive-time preview
	// must subtract them (drive time = arrived − began − paused stretches).
	const sortedPauses = [...pauseEvents].sort(
		(first, second) => moment(first.createdAt).valueOf() - moment(second.createdAt).valueOf()
	);
	const pauseEventTimes = sortedPauses.map((evt) => moment(evt.createdAt).valueOf());
	const earliestPauseMs = pauseEventTimes.length ? Math.min(...pauseEventTimes) : null;
	const latestPauseMs = pauseEventTimes.length ? Math.max(...pauseEventTimes) : null;

	const fmtClock = (value: string): string =>
		(arrivedTimeZone ? moment(value).tz(arrivedTimeZone) : moment(value)).format('h:mm A');

	// Pair paused → resumed into intervals for the read-only display and messages.
	const pauseIntervals: Array<{ start: string; end: string | null }> = [];
	let openPauseStart: string | null = null;
	for (const evt of sortedPauses) {
		if (evt.eventType === 'trip_paused') {
			openPauseStart = evt.createdAt;
			pauseIntervals.push({ start: evt.createdAt, end: null });
		} else if (evt.eventType === 'trip_resumed' && openPauseStart) {
			pauseIntervals[pauseIntervals.length - 1].end = evt.createdAt;
			openPauseStart = null;
		}
	}
	const pauseIntervalLabels = pauseIntervals.map(
		(interval) => `${fmtClock(interval.start)}–${interval.end ? fmtClock(interval.end) : 'ongoing'}`
	);

	// Total paused time; a pause left open (no resume) runs until arrival, mirroring
	// parseLocationEvents so the preview matches what the log shows after saving.
	const computePausedMs = (arrivedMs: number | null): number => {
		let total = 0;
		let openStart: number | null = null;
		for (const evt of sortedPauses) {
			const time = moment(evt.createdAt).valueOf();
			if (evt.eventType === 'trip_paused') {
				openStart = time;
			} else if (evt.eventType === 'trip_resumed' && openStart != null) {
				total += time - openStart;
				openStart = null;
			}
		}
		if (openStart != null && arrivedMs != null) total += Math.max(0, arrivedMs - openStart);
		return total;
	};

	const arrivedFieldValue = getFieldValue('arrivedTime');
	const arrivedPreviewMs = arrivedFieldValue
		? moment(wallTimeToUtcIso(arrivedFieldValue, arrivedTimeZone)).valueOf()
		: null;
	const beganPreviewMs = currentBeganTime ? moment(currentBeganTime).valueOf() : null;
	const netDriveMs =
		beganPreviewMs != null && arrivedPreviewMs != null
			? Math.max(0, arrivedPreviewMs - beganPreviewMs - computePausedMs(arrivedPreviewMs))
			: null;

	const beganDisabledDate = scheduleDisabledDate(beganTimeZone);
	const arrivedDisabledDate = scheduleDisabledDate(arrivedTimeZone);

	const handleSubmit = () => {
		form.validateFieldsAndScroll((err, values) => {
			if (err) return;

			const beganTimeZoneFinal = values.beganTimeZone || locationTimeZone;
			const arrivedTimeZoneFinal = values.arrivedTimeZone || locationTimeZone;

			const newBeganIso = formatDateForLocationTimeZone(values.beganTime, beganTimeZoneFinal);
			const newArrivedIso = formatDateForLocationTimeZone(values.arrivedTime, arrivedTimeZoneFinal);
			const newBeganMs = moment(newBeganIso).valueOf();
			const newArrivedMs = moment(newArrivedIso).valueOf();

			// The edited window must still contain the (fixed) pauses — otherwise the recomputed
			// drive time would subtract a pause that now sits outside the trip.
			if (
				(earliestPauseMs != null && newBeganMs > earliestPauseMs) ||
				(latestPauseMs != null && newArrivedMs < latestPauseMs)
			) {
				message.error(
					`Your times must still include the trip's pause${
						pauseIntervalLabels.length > 1 ? 's' : ''
					} (${pauseIntervalLabels.join(', ')}).`
				);
				return;
			}

			// Two trips to the same site on the same day can't overlap — it scrambles which
			// events belong to which trip. Touching end-to-end is allowed.
			const overlapping = otherTripWindows.find((tripWindow) => {
				const otherBeganMs = moment(tripWindow.began).valueOf();
				const otherArrivedMs = moment(tripWindow.arrived).valueOf();
				return newBeganMs < otherArrivedMs && newArrivedMs > otherBeganMs;
			});
			if (overlapping) {
				message.error(
					`This overlaps another trip to this site (${fmtClock(overlapping.began)}–${fmtClock(
						overlapping.arrived
					)}). Adjust the times so the trips don't overlap.`
				);
				return;
			}

			const retimes: Array<{ id: number; createdAt: string }> = [];
			if (beganEvent && beganEvent.id != null) {
				retimes.push({ id: beganEvent.id, createdAt: newBeganIso });
			}
			if (arrivedEvent && arrivedEvent.id != null) {
				retimes.push({ id: arrivedEvent.id, createdAt: newArrivedIso });
			}

			if (retimes.length === 0) {
				onCancel();
				return;
			}

			setSubmitting(true);
			reviseDriveTime(retimes)
				.then(() => {
					message.success('Drive time updated.');
					if (onSuccess) onSuccess();
					onCancel();
				})
				.catch((errResp) => {
					message.error(typeof errResp === 'string' ? errResp : 'Failed to update drive time.');
				})
				.finally(() => setSubmitting(false));
		});
	};

	return (
		<Modal
			visible={visible}
			width={520}
			title="Update Drive Time"
			okText="Save"
			onCancel={onCancel}
			onOk={handleSubmit}
			closable={false}
			confirmLoading={submitting}
		>
			<Form layout="vertical" form={form}>
				<div className="pb-3">
					<DateTimeWithTimeZone
						form={form}
						location={location}
						displayName={'Began Trip'}
						timeZoneName={'beganTimeZone'}
						dateName={'beganTime'}
						initialValue={beganInitial}
						rules={[
							{
								type: 'object',
								required: true,
								message: 'Please select when the trip began.',
							},
						]}
						disabledDate={beganDisabledDate}
						disabledTime={getDisabledDateTime(undefined, beganTimeZone, moment())}
					/>
				</div>
				<DateTimeWithTimeZone
					form={form}
					location={location}
					displayName={endLabel}
					timeZoneName={'arrivedTimeZone'}
					dateName={'arrivedTime'}
					initialValue={arrivedInitial}
					rules={[
						{
							type: 'object',
							required: true,
							message: endRequiredMessage,
						},
						{
							message: endAfterStartMessage,
							validator: (_, value) => {
								if (!value || !currentBeganTime) return Promise.resolve();
								const arrivedUtc = moment(wallTimeToUtcIso(value, arrivedTimeZone)).utc();
								const beganUtc = moment(currentBeganTime).utc();
								if (!arrivedUtc.isAfter(beganUtc)) {
									return Promise.reject(new Error(endAfterStartMessage));
								}
								return Promise.resolve();
							},
						},
					]}
					disabledDate={arrivedDisabledDate}
					disabledTime={getDisabledDateTime(
						currentBeganTime ? moment(currentBeganTime) : undefined,
						arrivedTimeZone,
						moment()
					)}
				/>
				{pauseIntervalLabels.length > 0 ? (
					<div className="footnoteregular mb-1 text-character-secondary">
						Paused (kept as-is): {pauseIntervalLabels.join(', ')}
					</div>
				) : null}
				{netDriveMs != null ? (
					<div className="footnoteregular text-character-secondary">
						Drive time: {netDriveMs > 0 ? formatDuration(netDriveMs) : 'less than a minute'}
					</div>
				) : null}
			</Form>
		</Modal>
	);
};

const mapDispatchToProps = (dispatch) => ({
	reviseDriveTime: (retimes: Array<{ id: number; createdAt: string }>) =>
		dispatch(reviseDriveTimeForSupplier(retimes)),
});

export const ReviseDriveTimeModal = connect(
	null,
	mapDispatchToProps
)(Form.create<ReviseDriveTimeModalProps>()(ReviseDriveTimeModalInner));
